/**
 * Reconcile — asking the server whether this till's sales actually arrived.
 *
 * ── Why a till cannot audit itself ─────────────────────────────────────────
 * Every sync failure this product has had looked the same from inside the app:
 * healthy. A push refused with 429 and abandoned after three retries. A first
 * sync that aborted before it ever reached the push phase. A pull whose
 * errored table was swallowed while the cursor advanced past the rows it never
 * read. In each case the shop went on billing and the sales were nowhere.
 *
 * `is_synced` cannot catch any of that, because `is_synced` is the client's
 * own opinion and the client's own opinion is what was wrong. The only honest
 * check is to ask the other side what it actually holds.
 *
 * So: the server lists the invoice numbers it has for this store over a recent
 * window, the till lists its own, and the difference is the answer. Invoice
 * numbers rather than ids because a missing one names a sale the shopkeeper
 * can go and look at.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice } from '@/db/models/Billing';
import { API_URL } from '@/config';
import { useAppStore } from '@/store/useAppStore';

/** How far back to compare. The server clamps this to 90 days regardless. */
export const RECONCILE_WINDOW_DAYS = 7;

export interface ReconcileDiff {
  /**
   * Billed on this device, absent from the server. THE number that matters:
   * every one of these is a sale that exists in one browser and nowhere else.
   */
  missingOnServer: string[];
  /**
   * On the server, absent locally. Usually benign — another till's sales that
   * this device has not pulled yet — so it is reported separately and never
   * raised as an alarm.
   */
  missingLocally: string[];
  /** Invoice numbers compared on each side. */
  localCount: number;
  serverCount: number;
  /**
   * The server could not list everything in the window. A difference is then
   * unknown rather than missing, and must not be reported as a lost sale.
   */
  truncated: boolean;
  checkedAt: number;
}

/**
 * Compare two sets of invoice numbers.
 *
 * Pure, and deliberately the only place the judgement lives — what counts as
 * "missing" is the whole point of the feature, and it should be testable
 * without a database or a network.
 *
 * Case and surrounding space are normalised because the two sides have
 * travelled through different code: one was typed into a local record, the
 * other came back through JSON from Postgres.
 */
export function diffInvoiceNumbers(
  local: readonly string[],
  server: readonly string[],
  options: { truncated?: boolean } = {},
): Omit<ReconcileDiff, 'checkedAt'> {
  const norm = (n: string) => n.trim().toUpperCase();
  const localSet = new Set(local.map(norm).filter((n) => n !== ''));
  const serverSet = new Set(server.map(norm).filter((n) => n !== ''));

  const truncated = options.truncated === true;

  // When the server truncated its list, an invoice absent from it may simply
  // be past the cut. Claiming those are missing would cry wolf on every busy
  // shop, and a warning nobody believes is worse than no warning.
  const missingOnServer = truncated
    ? []
    : [...localSet].filter((n) => !serverSet.has(n)).sort();

  const missingLocally = [...serverSet].filter((n) => !localSet.has(n)).sort();

  return {
    missingOnServer,
    missingLocally,
    localCount: localSet.size,
    serverCount: serverSet.size,
    truncated,
  };
}

/** This store's invoice numbers from the local database, for the same window. */
export async function localInvoiceNumbers(
  storeId: string,
  sinceMs: number,
): Promise<string[]> {
  const rows = await database
    .get<Invoice>('invoices')
    .query(
      Q.where('store_id', storeId),
      Q.where('is_deleted', false),
      Q.where('created_at', Q.gte(sinceMs)),
    )
    .fetch();
  return rows.map((r) => r.invoiceNumber).filter(Boolean);
}

interface ReconcileResponse {
  since: string;
  invoiceNumbers: string[];
  count: number;
  truncated: boolean;
}

/**
 * Run the check. Returns null when it could not be done — offline, refused,
 * no store — because "I could not check" and "nothing is missing" must never
 * look the same to the caller.
 */
export async function reconcileStore(
  storeId: string | null,
  windowDays = RECONCILE_WINDOW_DAYS,
): Promise<ReconcileDiff | null> {
  if (!storeId) return null;

  try {
    const token = useAppStore.getState().authToken;
    if (!token) return null;

    const res = await fetch(
      `${API_URL}/sync/reconcile?storeId=${encodeURIComponent(storeId)}&sinceDays=${windowDays}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(15_000),
      },
    );
    // A 401 here is not worth a token refresh dance: the next real sync does
    // that, and a reconcile that silently skips one round is harmless. What is
    // NOT harmless is reporting "nothing missing" when we never asked.
    if (!res.ok) return null;

    const body = (await res.json()) as ReconcileResponse;
    // The server decides the window it actually answered for — it clamps — so
    // the local side is cut to the SAME boundary. Comparing a local week
    // against a server day would invent missing sales out of arithmetic.
    const sinceMs = new Date(body.since).getTime();
    if (!Number.isFinite(sinceMs)) return null;

    const local = await localInvoiceNumbers(storeId, sinceMs);
    return {
      ...diffInvoiceNumbers(local, body.invoiceNumbers ?? [], { truncated: body.truncated }),
      checkedAt: Date.now(),
    };
  } catch {
    // Offline is the normal case for a till, not an error worth shouting about.
    return null;
  }
}
